import { cacheGet, cacheSet } from "./offlineStore";
import { getOfflineSessionScope, isOffline } from "./offlineQueue";

const CACHE_SCOPE_VERSION = "v2";

function scopeCacheKey(key) {
  const normalizedKey = String(key || "cache");
  if (normalizedKey.startsWith(`${CACHE_SCOPE_VERSION}:user:`)) {
    return normalizedKey;
  }

  const { userId, companyId } = getOfflineSessionScope();
  return `${CACHE_SCOPE_VERSION}:user:${userId || "anonymous"}:company:${
    companyId || "unscoped"
  }:${normalizedKey}`;
}

export function isLikelyNetworkError(error) {
  if (!error) return false;
  if (error?.response) return false;

  const normalizedCode = String(error?.code || "").toUpperCase();
  if (
    normalizedCode === "ERR_NETWORK" ||
    normalizedCode === "ECONNABORTED" ||
    normalizedCode === "ETIMEDOUT"
  ) {
    return true;
  }

  const normalizedMessage = String(error?.message || error)
    .trim()
    .toLowerCase();

  return (
    normalizedMessage.includes("network error") ||
    normalizedMessage.includes("failed to fetch") ||
    normalizedMessage.includes("load failed") ||
    normalizedMessage.includes("network request failed")
  );
}

export function buildCacheKey(base, params) {
  if (!params) return scopeCacheKey(base);
  try {
    const sorted = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        const value = params[key];
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {});
    return scopeCacheKey(`${base}:${JSON.stringify(sorted)}`);
  } catch (error) {
    return scopeCacheKey(base);
  }
}

export async function fetchWithCacheMetadata(key, fetcher, fallback) {
  const fallbackValue = fallback === undefined ? null : fallback;
  const scopedKey = scopeCacheKey(key);

  if (isOffline()) {
    const cached = await cacheGet(scopedKey);
    return cached
      ? {
          data: cached.value,
          status: "cached",
          updatedAt: cached.updatedAt ?? null,
        }
      : {
          data: fallbackValue,
          status: "unavailable",
          updatedAt: null,
        };
  }

  try {
    const data = await fetcher();
    const cachedEntry = await cacheSet(scopedKey, data);
    return {
      data,
      status: "verified",
      updatedAt: cachedEntry?.updatedAt ?? Date.now(),
    };
  } catch (error) {
    if (!isLikelyNetworkError(error)) {
      throw error;
    }

    const cached = await cacheGet(scopedKey);
    if (cached) {
      return {
        data: cached.value,
        status: "cached",
        updatedAt: cached.updatedAt ?? null,
      };
    }

    throw error;
  }
}

export async function fetchWithCache(key, fetcher, fallback) {
  const result = await fetchWithCacheMetadata(key, fetcher, fallback);
  return result.data;
}
